import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { StyleSheet, ViewStyle } from 'react-native';

import { Box } from '@components/Restyle';
import theme from '@themes/theme';

export const SLIDE_IN_PANEL_DURATION_MS = 360;
// Decelerating "push" curve in, accelerating curve out.
const EASE_IN = 'cubic-bezier(0.22, 1, 0.36, 1)';
const EASE_OUT = 'cubic-bezier(0.64, 0, 0.78, 0)';

// RN Web turns `animationKeyframes` into a CSS @keyframes rule, so the motion
// runs on the compositor from the first painted frame. A JS-driven
// Animated.timing started in useEffect loses its opening frames to the heavy
// panel mount (Create Project form, task detail) and visibly jumps.
// `animationKeyframes` only works in StyleSheet.create — RN Web's inline-style
// path silently drops it — so these must stay compiled sheets, not literals.
// The casts are needed because these are RN Web-only style keys.
const motion = (
  keyframes: Record<string, unknown>[],
  easing: string
): ViewStyle =>
  ({
    animationKeyframes: keyframes,
    animationDuration: `${SLIDE_IN_PANEL_DURATION_MS}ms`,
    animationTimingFunction: easing,
    animationFillMode: 'both',
  }) as unknown as ViewStyle;

const panelIn = {
  from: { transform: 'translateX(100%)' },
  to: { transform: 'none' },
};
const panelOut = {
  from: { transform: 'none' },
  to: { transform: 'translateX(100%)' },
};
// A backdrop that wraps its panel (the onboarding Overlay) animates background
// colour, not opacity, so the nested panel is not faded along with it.
const dimIn = {
  from: { backgroundColor: 'transparent' },
  to: { backgroundColor: theme.colors.grey06 },
};
const dimOut = {
  from: { backgroundColor: theme.colors.grey06 },
  to: { backgroundColor: 'transparent' },
};
// A standalone backdrop (SlideBackdropBox, no children) fades with opacity
// instead: that runs on the compositor like the panel's transform, while a
// background-colour animation repaints on the main thread and stutters when
// the panel mounts heavy content mid-slide (Checklist Template, TA-10891 QA).
const fadeIn = { from: { opacity: 0 }, to: { opacity: 1 } };
const fadeOut = { from: { opacity: 1 }, to: { opacity: 0 } };

const styles = StyleSheet.create({
  panelIn: motion([panelIn], EASE_IN),
  panelOut: motion([panelOut], EASE_OUT),
  backdropIn: {
    backgroundColor: theme.colors.grey06,
    ...motion([dimIn], 'ease-out'),
  },
  backdropOut: {
    backgroundColor: 'transparent',
    ...motion([dimOut], 'ease-in'),
  },
  scrimIn: {
    backgroundColor: theme.colors.grey06,
    ...motion([fadeIn], 'ease-out'),
  },
  scrimOut: {
    backgroundColor: theme.colors.grey06,
    opacity: 0,
    ...motion([fadeOut], 'ease-in'),
  },
});

type SlidePhase = 'in' | 'out';
const SlidePhaseContext = createContext<SlidePhase>('in');

/**
 * Side-panel motion for the enclosing SlidePanelPresence (TA-10891 #13/#15):
 * add `panel` to the panel's own style and `backdrop` to its dimmed backdrop.
 * Outside a presence it is the push-in pair.
 */
export const useSlidePanelMotion = () => {
  const phase = useContext(SlidePhaseContext);
  return phase === 'in'
    ? { panel: styles.panelIn, backdrop: styles.backdropIn }
    : { panel: styles.panelOut, backdrop: styles.backdropOut };
};

interface SlidePanelPresenceProps {
  open: boolean;
  children?: React.ReactNode;
}

/**
 * Mounts a right-docked side panel with a push-in, and on close keeps the
 * last rendered panel on screen while it slides back out, then unmounts it.
 * The callers' open flags (and the ids that pick the panel's content) are
 * already cleared by then, so the frozen element is what keeps the panel from
 * going blank mid-slide. The motion itself is read through context by
 * SlidePanelBox / SlideBackdropBox / useSlidePanelMotion, which re-render
 * inside the frozen element when the phase flips.
 */
export const SlidePanelPresence: React.FC<SlidePanelPresenceProps> = ({
  open,
  children,
}) => {
  const [mounted, setMounted] = useState(open);
  const lastChildren = useRef<React.ReactNode>(open ? children : null);
  if (open) lastChildren.current = children;

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    const timer = setTimeout(() => {
      lastChildren.current = null;
      setMounted(false);
    }, SLIDE_IN_PANEL_DURATION_MS);
    return () => clearTimeout(timer);
  }, [open]);

  if (!open && !mounted) return null;
  return (
    <SlidePhaseContext.Provider value={open ? 'in' : 'out'}>
      {lastChildren.current}
    </SlidePhaseContext.Provider>
  );
};

type BoxProps = React.ComponentProps<typeof Box>;

/** Restyle Box for the panel surface, carrying the current slide motion. */
export const SlidePanelBox: React.FC<BoxProps> = ({ style, ...rest }) => {
  const { panel } = useSlidePanelMotion();
  return <Box {...rest} style={[style, panel]} />;
};

/**
 * Restyle Box for a standalone dimmed backdrop, carrying the current fade
 * motion. Opacity-based, so it must not wrap the panel — a backdrop with
 * children uses `useSlidePanelMotion().backdrop` instead.
 */
export const SlideBackdropBox: React.FC<BoxProps> = ({ style, ...rest }) => {
  const phase = useContext(SlidePhaseContext);
  return (
    <Box
      {...rest}
      style={[style, phase === 'in' ? styles.scrimIn : styles.scrimOut]}
    />
  );
};
